import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { calcLocal, calcTotals, defaultSpec } from "./order/pricing";
import { OrderStep1Upload } from "./order/OrderStep1Upload";
import { OrderStep2Details } from "./order/OrderStep2Details";
import { OrderStep3Pay } from "./order/OrderStep3Pay";
import { OrderSummary } from "./order/OrderSummary";
import { devLog } from "@/lib/devLog";

export default function Order() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [items, setItems] = useState([]); // [{ file, spec }]
  const [step, setStep] = useState(1); // 1=upload/config, 2=checkout, 3=pay

  // Scroll to top whenever the checkout step changes so the user never lands
  // mid-page (especially when going Back from payment → details, the page
  // would otherwise still be scrolled to the bottom).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);

  // Checkout fields
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [deliveryCounty, setDeliveryCounty] = useState("");
  const [deliveryTown, setDeliveryTown] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryRecipientName, setDeliveryRecipientName] = useState("");
  const [deliveryRecipientPhone, setDeliveryRecipientPhone] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [tier, setTier] = useState("normal");
  const [notes, setNotes] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [business, setBusiness] = useState(null); // {account:{name,plan_label,discount_pct,wallet_kes}}

  useEffect(() => {
    api.get("/business-accounts/me")
      .then((r) => setBusiness(r.data?.linked ? r.data : null))
      .catch(() => {});
  }, []);

  // Subtotal-only for promo validation (server recomputes the authoritative total)
  const clientSubtotal = items.reduce((s, x) => s + (x.spec?.estimated_cost || 0), 0);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code: promoInput.trim(),
        subtotal: clientSubtotal,
      });
      setAppliedPromo(data);
      toast.success(`${data.code} · -KES ${data.discount_amount}`);
    } catch (e) {
      setAppliedPromo(null);
      toast.error(formatApiError(e?.response?.data?.detail) || "Invalid code");
    } finally {
      setApplyingPromo(false);
    }
  };
  const removePromo = () => { setAppliedPromo(null); setPromoInput(""); };

  // Payment
  const [order, setOrder] = useState(null);
  const [payPhone, setPayPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [mpesaConfig, setMpesaConfig] = useState(null);
  const [creating, setCreating] = useState(false);
  const [trackCopied, setTrackCopied] = useState(false);
  const [reorderHint, setReorderHint] = useState(null); // {tracking, item_count}

  // Reorder prefill — Dashboard.handleReorder stashes a template; we read it
  // once on mount and prefill every field except the actual files (purge-safe).
  useEffect(() => {
    let stash;
    try { stash = sessionStorage.getItem("ctp_reorder_template"); } catch { stash = null; }
    if (!stash) return;
    let tpl;
    try { tpl = JSON.parse(stash); } catch { return; }
    sessionStorage.removeItem("ctp_reorder_template");
    if (tpl.customer_name) setCustomerName(tpl.customer_name);
    if (tpl.customer_phone) setCustomerPhone(tpl.customer_phone);
    if (tpl.customer_email) setCustomerEmail(tpl.customer_email);
    if (tpl.delivery_method) setDeliveryMethod(tpl.delivery_method);
    if (tpl.delivery_county) setDeliveryCounty(tpl.delivery_county);
    if (tpl.delivery_town) setDeliveryTown(tpl.delivery_town);
    if (tpl.delivery_address) setDeliveryAddress(tpl.delivery_address);
    if (tpl.delivery_recipient_name) setDeliveryRecipientName(tpl.delivery_recipient_name);
    if (tpl.delivery_recipient_phone) setDeliveryRecipientPhone(tpl.delivery_recipient_phone);
    if (tpl.delivery_instructions) setDeliveryInstructions(tpl.delivery_instructions);
    if (tpl.tier) setTier(tpl.tier);
    if (tpl.notes) setNotes(tpl.notes);
    setReorderHint({
      tracking: tpl.source_tracking_number,
      item_count: (tpl.items_template || []).length,
      items_template: tpl.items_template || [],
    });
  }, []);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data));
    api.get("/payments/mpesa/config").then((r) => setMpesaConfig(r.data));
  }, []);

  useEffect(() => {
    if (user) {
      setCustomerName(user.name || "");
      setCustomerPhone(user.phone || "");
      setCustomerEmail(user.email || "");
    }
  }, [user]);

  const onUploaded = (file) => {
    setItems((arr) => {
      // If a reorder template exists, drain the next template entry to prefill
      // specs for this freshly-uploaded file. After all templates are consumed
      // we fall back to defaults.
      let prefilled = defaultSpec(file);
      let nextHint = reorderHint;
      if (reorderHint && reorderHint.items_template && reorderHint.items_template.length > 0) {
        const [tpl, ...rest] = reorderHint.items_template;
        prefilled = {
          ...prefilled,
          paper_size: tpl.paper_size || prefilled.paper_size,
          color_mode: tpl.color_mode || prefilled.color_mode,
          double_sided: tpl.double_sided ?? prefilled.double_sided,
          stapling: !!tpl.stapling,
          binding: tpl.binding || "none",
          binding_cover: tpl.binding_top_cover || tpl.binding_cover || null,
          binding_top_cover: tpl.binding_top_cover || tpl.binding_cover || null,
          binding_back_cover: tpl.binding_back_cover || null,
          binding_gold_engraving: !!tpl.binding_gold_engraving,
          binding_engraving_source: tpl.binding_engraving_source || null,
          lamination: !!tpl.lamination,
          copies: Math.max(1, parseInt(tpl.copies || 1)),
        };
        nextHint = { ...reorderHint, items_template: rest };
        setReorderHint(nextHint);
      }
      return [...arr, { file, spec: prefilled }];
    });
  };

  const removeFile = (id) => {
    setItems((arr) => arr.filter((x) => x.file.id !== id));
  };

  const updateSpec = (id, patch) => {
    setItems((arr) =>
      arr.map((x) => {
        if (x.file.id !== id) return x;
        const next = { ...x.spec, ...patch };
        // Switching color mode: reset the colour-pages list (fresh detection
        // for auto, empty for custom). Keeps the pricing preview in a valid
        // state and prevents ghost pages from a previous mode leaking through.
        if (patch.color_mode && patch.color_mode !== x.spec.color_mode) {
          next.color_pages = [];
        }
        // Binding ⊕ (Lamination | Stapling) — binding cannot coexist with either
        if (patch.binding && patch.binding !== "none") {
          next.lamination = false;
          next.stapling = false;
          // Default covers: blue for spiral/tape; hardcover uses solid blue too.
          if (!next.binding_top_cover) next.binding_top_cover = "blue";
          if (patch.binding !== "hardcover" && !next.binding_back_cover) {
            next.binding_back_cover = "blue";
          }
          // Hardcover uses a single cover — the "back" field is unused.
          // Gold engraving is also hardcover-only.
          if (patch.binding === "hardcover") {
            next.binding_back_cover = null;
          } else {
            // Switching AWAY from hardcover → wipe engraving state
            next.binding_gold_engraving = false;
            next.binding_engraving_source = null;
            next.binding_engraving_file_id = null;
            next.binding_engraving_file_name = null;
          }
        }
        if (patch.binding === "none") {
          next.binding_top_cover = null;
          next.binding_back_cover = null;
          next.binding_gold_engraving = false;
          next.binding_engraving_source = null;
          next.binding_engraving_file_id = null;
          next.binding_engraving_file_name = null;
        }
        if (patch.lamination === true || patch.stapling === true) {
          next.binding = "none";
          next.binding_top_cover = null;
          next.binding_back_cover = null;
          next.binding_gold_engraving = false;
          next.binding_engraving_source = null;
          next.binding_engraving_file_id = null;
          next.binding_engraving_file_name = null;
        }
        // Stapling ⊕ Lamination — physically incompatible finishes.
        // Turning one ON must switch the other OFF.
        if (patch.stapling === true) next.lamination = false;
        if (patch.lamination === true) next.stapling = false;
        // Clearing engraving checkbox wipes downstream state.
        if (patch.binding_gold_engraving === false) {
          next.binding_engraving_source = null;
          next.binding_engraving_file_id = null;
          next.binding_engraving_file_name = null;
        }
        // Keep legacy `binding_cover` in sync with the top cover so backend
        // orders logged before this feature keep resolving cleanly.
        next.binding_cover = next.binding_top_cover;
        // Batching is only meaningful with binding or stapling AND ≥ 2 copies.
        // Auto-disable if the user removes the finishing method or drops
        // copies to 1 — prevents "batch on, but nothing to batch" states.
        const batchStillValid =
          (next.binding !== "none" || next.stapling) &&
          Math.max(1, Number(next.copies || 1)) > 1;
        if (next.batch_copies && !batchStillValid) {
          next.batch_copies = false;
        }
        return { ...x, spec: next };
      })
    );
  };

  const updatePages = (id, pages) => {
    setItems((arr) =>
      arr.map((x) =>
        x.file.id === id
          ? { ...x, file: { ...x.file, pages }, spec: { ...x.spec, pages } }
          : x
      )
    );
  };

  const itemTotals = useMemo(() => {
    if (!pricing) return [];
    return items.map((x) => calcLocal(x.spec, pricing));
  }, [items, pricing]);

  const subtotal = itemTotals.reduce((a, b) => a + b, 0);
  const { tieredSubtotal, fee, total, mult } = calcTotals(subtotal, tier, pricing);

  // Business / Office Pack: applied AFTER tiered subtotal + fee, BEFORE promo
  // (mirrors backend logic). Min-order clamp applies after.
  const minOrder = Number(pricing?.min_order ?? 0);
  const businessDiscountPct = business?.account?.discount_pct || 0;
  const businessDiscountAmount = businessDiscountPct > 0
    ? Math.round((total * businessDiscountPct) / 100)
    : 0;
  const totalAfterBusiness = businessDiscountAmount > 0
    ? Math.max(total - businessDiscountAmount, minOrder)
    : total;
  const walletBalance = business?.account?.wallet_kes || 0;
  const willPayFromWallet = !!business && walletBalance >= totalAfterBusiness;
  const walletAfter = willPayFromWallet ? walletBalance - totalAfterBusiness : walletBalance;

  const canProceed = items.length > 0 && pricing && subtotal > 0;

  const submitOrder = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    if (deliveryMethod === "nairobi_delivery" && (!deliveryTown.trim() || !deliveryAddress.trim())) {
      toast.error("Please fill in delivery town and address");
      return;
    }
    if (deliveryMethod === "countrywide" && (!deliveryCounty.trim() || !deliveryTown.trim() || !deliveryAddress.trim())) {
      toast.error("Please fill in delivery county, town and address");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail || null,
        delivery_method: deliveryMethod,
        delivery_address: deliveryMethod !== "pickup" ? deliveryAddress : null,
        delivery_county: deliveryMethod === "countrywide" ? deliveryCounty : null,
        delivery_town: deliveryMethod !== "pickup" ? deliveryTown : null,
        delivery_recipient_name: deliveryMethod !== "pickup" ? (deliveryRecipientName || customerName) : null,
        delivery_recipient_phone: deliveryMethod !== "pickup" ? (deliveryRecipientPhone || customerPhone) : null,
        delivery_instructions: deliveryMethod !== "pickup" ? (deliveryInstructions || null) : null,
        tier,
        items: items.map((x) => x.spec),
        notes: notes || null,
        promo_code: appliedPromo?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
      setPayPhone(customerPhone);
      setStep(3);
      toast.success(`Order created · ${data.tracking_number}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create order");
    }
    setCreating(false);
  };

  const copyTracking = async () => {
    if (!order?.tracking_number) return;
    try {
      await navigator.clipboard.writeText(order.tracking_number);
      setTrackCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setTrackCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const initiatePay = async () => {
    if (!order || !payPhone) return;
    setPaying(true);
    try {
      const { data } = await api.post("/payments/mpesa/initiate", {
        order_id: order.id,
        phone_number: payPhone,
      });
      toast.success(data.message || "Payment initiated");
      if (data.placeholder_mode) {
        setTimeout(() => navigate(`/track?ref=${order.tracking_number}`), 1500);
      } else {
        // Poll for status
        const tick = async () => {
          try {
            const { data: ps } = await api.get(`/payments/order/${order.id}`);
            if (ps.payment_status === "paid") {
              toast.success("Payment received!");
              navigate(`/track?ref=${order.tracking_number}`);
              return;
            }
          } catch (err) { devLog.debug("payment-poll failed", err); /* keep polling */ }
          setTimeout(tick, 4000);
        };
        setTimeout(tick, 5000);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Payment failed to initiate");
    }
    setPaying(false);
  };

  const confirmPayment = async () => {
    if (!order) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/payments/order/${order.id}/confirm`);
      if (data.confirmed) {
        toast.success(data.already_paid ? "Already paid — taking you to tracking." : (data.message || "Payment confirmed!"));
        setTimeout(() => navigate(`/track?ref=${order.tracking_number}`), 800);
      } else if (data.still_processing) {
        toast.info(data.message || "Still processing — try again shortly.");
      } else {
        toast.error(data.message || "Not paid yet — please complete the STK on your phone.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't confirm payment");
    }
    setConfirming(false);
  };

  if (!pricing) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#059669]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {[
          { n: 1, label: "Upload & Configure", short: "Upload" },
          { n: 2, label: "Your Details", short: "Details" },
          { n: 3, label: "Pay with M-Pesa", short: "Pay" },
        ].map((s, idx, arr) => (
          <div key={s.n} className="flex items-center gap-2 flex-shrink-0">
            <div className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 border ${step >= s.n ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252]"}`}>
              <span className="mono text-xs">0{s.n}</span>
              <span className="text-xs uppercase tracking-[0.15em] font-bold hidden sm:inline">{s.label}</span>
              <span className="text-xs uppercase tracking-[0.15em] font-bold sm:hidden">{s.short}</span>
            </div>
            {idx < arr.length - 1 && <div className="w-4 sm:w-6 h-px bg-[#E5E5E5]" />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {step === 1 && (
            <OrderStep1Upload
              items={items}
              itemTotals={itemTotals}
              pricing={pricing}
              reorderHint={reorderHint}
              clearReorderHint={() => setReorderHint(null)}
              onUploaded={onUploaded}
              removeFile={removeFile}
              updateSpec={updateSpec}
              updatePages={updatePages}
              canProceed={canProceed}
              goToDetails={() => setStep(2)}
            />
          )}

          {step === 2 && (
            <OrderStep2Details
              pricing={pricing}
              totalAfterBusiness={totalAfterBusiness}
              customerName={customerName} setCustomerName={setCustomerName}
              customerPhone={customerPhone} setCustomerPhone={setCustomerPhone}
              customerEmail={customerEmail} setCustomerEmail={setCustomerEmail}
              deliveryMethod={deliveryMethod} setDeliveryMethod={setDeliveryMethod}
              deliveryCounty={deliveryCounty} setDeliveryCounty={setDeliveryCounty}
              deliveryTown={deliveryTown} setDeliveryTown={setDeliveryTown}
              deliveryAddress={deliveryAddress} setDeliveryAddress={setDeliveryAddress}
              deliveryRecipientName={deliveryRecipientName} setDeliveryRecipientName={setDeliveryRecipientName}
              deliveryRecipientPhone={deliveryRecipientPhone} setDeliveryRecipientPhone={setDeliveryRecipientPhone}
              deliveryInstructions={deliveryInstructions} setDeliveryInstructions={setDeliveryInstructions}
              tier={tier} setTier={setTier}
              notes={notes} setNotes={setNotes}
              promoInput={promoInput} setPromoInput={setPromoInput}
              appliedPromo={appliedPromo}
              applyPromo={applyPromo}
              removePromo={removePromo}
              applyingPromo={applyingPromo}
              creating={creating}
              goBack={() => setStep(1)}
              submitOrder={submitOrder}
            />
          )}

          {step === 3 && order && (
            <OrderStep3Pay
              order={order}
              mpesaConfig={mpesaConfig}
              total={total}
              payPhone={payPhone} setPayPhone={setPayPhone}
              paying={paying} initiatePay={initiatePay}
              confirming={confirming} confirmPayment={confirmPayment}
              trackCopied={trackCopied} copyTracking={copyTracking}
              goBack={() => setStep(2)}
            />
          )}
        </div>

        <OrderSummary
          items={items}
          itemTotals={itemTotals}
          pricing={pricing}
          subtotal={subtotal}
          tier={tier}
          mult={mult}
          tieredSubtotal={tieredSubtotal}
          fee={fee}
          total={total}
          business={business}
          businessDiscountPct={businessDiscountPct}
          businessDiscountAmount={businessDiscountAmount}
          totalAfterBusiness={totalAfterBusiness}
          walletBalance={walletBalance}
          walletAfter={walletAfter}
          willPayFromWallet={willPayFromWallet}
        />
      </div>
    </div>
  );
}
